/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import ImageLayer from 'ol/layer/Image';
import { useContext, useEffect, useState } from 'react';
import { LayerType, WMDefaultStyleName } from '@opengeoweb/webmap';
import type { Dimension, Style } from '@opengeoweb/webmap';
import { useGetWMLayerInstance } from '../../../hooks/useHooksGetCapabilities';
import MapContext from '../context/MapContext';
import TimeContext from '../context/TimeContext';
import {
  getDimensionParamsForWMS,
  makeDimValuesToPrefetch,
} from '../utils/makeDimValuesToPrefetch';
import { ShowTimeAwareLayerInfo } from '../utils/ShowTimeAwareLayerInfo';
import { TimeawareImageSource } from '../utils/TimeawareImageSource';
import type { OnInitializeLayerProps } from '../utils/types';

interface TimeawareImageSourceWMSLayerProps {
  layerId?: string;
  getCapsURL: string;
  layerName: string;
  styleName?: string;
  layerOptions: ConstructorParameters<typeof ImageLayer>[0];
  dimensions?: Dimension[];
  visible?: boolean;
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  onLayerReady?: (layerId: string) => void;
  onLayerError?: (layerId: string, message: string) => void;
  debugMode?: boolean;
  extentRatio?: number;
}

export const getWMSStyleName = (
  styleName: string | undefined,
  styles: Style[],
): string => {
  if (styleName && styles.some((style) => style.name === styleName)) {
    return styleName;
  }
  return styles.some((style) => style.name === WMDefaultStyleName)
    ? WMDefaultStyleName
    : '';
};

export const TimeawareImageSourceWMSLayer: React.FC<
  TimeawareImageSourceWMSLayerProps
> = ({
  layerId,
  getCapsURL,
  layerOptions,
  styleName,
  layerName,
  dimensions = [],
  debugMode,
  visible = true,
  onInitializeLayer,
  onLayerReady,
  onLayerError,
  extentRatio,
}: TimeawareImageSourceWMSLayerProps) => {
  const { map } = useContext(MapContext);
  const { isAnimating, prefetchList } = useContext(TimeContext);
  const [layer, setLayer] = useState<ImageLayer<TimeawareImageSource>>();

  // get a wmLayer instance
  const { wmLayer, errorMessage, isParsedForName } = useGetWMLayerInstance({
    service: getCapsURL,
    name: layerName,
    id: layerId,
    layerType: LayerType.mapLayer,
  });
  if (errorMessage && onLayerError) {
    onLayerError(layerId || '', errorMessage);
  }
  // dimensionsValues is a key/value dictionary with dimension names specifically for WMS GetMap requests
  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);

  // This string can be used safely in a useEffect or other React hook to check if the contents changed
  const dimensionsValuesEffectCheck =
    dimensionsValues &&
    (Object.entries(dimensionsValues)
      .map((dimensionNameAndCurrentValue) => {
        return `${dimensionNameAndCurrentValue[0]}:${dimensionNameAndCurrentValue[1]}`;
      })
      .join(',') as string);

  const prefetchListEffectCheck = (prefetchList ?? []).join(',');

  // Make a TimeawareImageSource and add it to the map, add and remove should only be done when the map changes, layer id changes, visibility changes or dimensionsValues get defined.
  useEffect(() => {
    if (!map || !wmLayer || !visible || !dimensionsValues) {
      return (): void => {};
    }

    // When this component mounts, an ImageLayer is added to the map. Once it unmounts, this layer is removed from the map.
    const olLayer = new ImageLayer<TimeawareImageSource>({
      source: new TimeawareImageSource({
        wmsUrl: wmLayer.getmapURL,
        layerName: wmLayer.name!,
        layerId: layerId || '',
        styleName: getWMSStyleName(styleName, wmLayer.getStyles()),
        dimProps: dimensionsValues,
        timeStampsToPrefetch: [],
        visible,
        extentRatio,
      }),
    });
    map.addLayer(olLayer);
    setLayer(olLayer);
    // Assign olSource in wmlayer for metronome
    wmLayer.olSource = olLayer?.getSource();
    return (): void => {
      const olSource = olLayer.getSource();
      olSource?.cancelPrefetch();
      wmLayer.olSource = null;
      map.removeLayer(olLayer);
      olLayer.dispose();
      olSource?.dispose();
    };

    // The dependency list should only contain the map, wmLayer, visible and dimensionValues. Other prop changes are handled by other effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, wmLayer, visible, dimensionsValues === undefined]);

  useEffect(() => {
    onLayerReady && wmLayer && layerId && onLayerReady(layerId);
  }, [layerId, wmLayer, onLayerReady]);

  // Initialize layer for GeoWeb, call onInitializeLayer
  useEffect(() => {
    const initializeLayer = async (): Promise<void> => {
      if (wmLayer) {
        const wmTimeDim = wmLayer?.dimensions?.find(
          (dimension) => dimension.name === 'time',
        );
        // Set time dimension current value based on layerprops dimension value

        const layerPropsTimeDim = dimensions?.find(
          (dimension) => dimension.name === 'time',
        );

        const timeValueToInitialize =
          layerPropsTimeDim?.currentValue || wmTimeDim?.getValue();
        wmTimeDim &&
          timeValueToInitialize &&
          wmTimeDim.setValue(timeValueToInitialize);

        const refTimeDim = wmLayer?.dimensions?.find(
          (dimension) => dimension.name === 'reference_time',
        );

        // Update reference time dimension value, this will change the time values to prefetch. Depends on model run.
        if (refTimeDim && wmTimeDim) {
          const layerPropsRefTimeDim = dimensions?.find(
            (dimension) => dimension.name === 'reference_time',
          );
          // Set reftime dimension current value based on layerprops dimension value
          refTimeDim &&
            layerPropsRefTimeDim?.currentValue &&
            refTimeDim.setValue(layerPropsRefTimeDim?.currentValue);

          const refTimeToFind = wmTimeDim.getValue();
          const firstRefTime = refTimeDim?.getClosestValue(refTimeToFind, true);

          firstRefTime &&
            refTimeDim &&
            wmTimeDim?.setTimeValuesForReferenceTime(firstRefTime, refTimeDim);
        }

        // Make sure we have the latest dimension values before initializing
        await wmLayer.parseLayer();

        // Make sure style is set in wmLayer as well. Done after parsing, so the style is checked against the styles of this layer name.
        styleName && wmLayer.setStyle(styleName);

        onInitializeLayer?.({
          layerId: wmLayer.id,
          style: wmLayer.currentStyle!,
          dimensions: wmLayer.dimensions.map((dimension) => {
            return {
              name: dimension.name,
              units: dimension.units,
              currentValue: dimension.currentValue,
              maxValue: dimension.getLastValue(),
              minValue: dimension.getFirstValue(),
              synced: dimension.synced,
              values: dimension.values,
              unitSymbol: dimension.unitSymbol,
            };
          }),
        });
      }
    };
    initializeLayer().catch((e) => window.console.error(e));

    return (): void => {};

    // Effect array should only contain wmLayer and layerName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wmLayer, layerName]);

  // Layer opacity and zIndex useEffect
  useEffect(() => {
    if (!layerOptions || !layer) {
      return;
    }

    if (layerOptions.opacity !== undefined && layerOptions.opacity !== null) {
      layer.setOpacity(layerOptions.opacity);
    }
    if (layerOptions.zIndex !== undefined && layerOptions.zIndex !== null) {
      layer.setZIndex(layerOptions.zIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, layerOptions?.zIndex, layerOptions?.opacity]);

  // Layer visible useEffect
  useEffect(() => {
    layer?.setVisible(visible);
  }, [layer, visible]);

  // Layer name, style and dim values useEffect.
  // After a layer name change, this waits until the wmLayer is parsed for the new name. Until then the wmLayer still has the styles and dimensions of the previous layer.
  // Name, style and dimensions are set in the source at once, so it never requests the new layer with the style or dimensions of the previous one.
  useEffect(() => {
    if (!wmLayer || !isParsedForName) {
      return;
    }
    const styles = wmLayer.getStyles();
    const style = getWMSStyleName(styleName, styles);

    wmLayer.setStyle(style || styles[0]?.name || '');
    dimensionsValues &&
      layer?.getSource()?.setLayerProps({
        layerName,
        styleName: style,
        dimProps: dimensionsValues,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layer,
    wmLayer,
    isParsedForName,
    layerName,
    styleName,
    dimensionsValuesEffectCheck,
  ]);

  // Prefetch useEffect
  useEffect(() => {
    if (!wmLayer || !isParsedForName) {
      return;
    }
    const wmTimeDim = wmLayer?.dimensions?.find(
      (dimension) => dimension.name === 'time',
    );
    if (!wmTimeDim) {
      return;
    }
    try {
      const dimensionValuesToPrefetch = makeDimValuesToPrefetch(
        wmTimeDim,
        prefetchList,
        isAnimating,
      );
      layer
        ?.getSource()
        ?.setTimeValuesValuesToPrefetch(dimensionValuesToPrefetch);
    } catch (e) {
      window.console.warn(e);
    }
    // The prefetch useeffect should be triggered when one of the following change:
    // - layer,
    // - wmLayer
    // - prefetchList
    // - styleName,
    // - dimensionsValuesEffectCheck,
    // - isParsedForName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layer,
    wmLayer,
    prefetchListEffectCheck,
    layerName,
    styleName,
    dimensionsValuesEffectCheck,
    isParsedForName,
  ]);

  // Only show debug info for data layers with time dimension
  const hasTimeDim =
    wmLayer?.dimensions?.findIndex((dimension) => dimension.name === 'time') ??
    -1;

  return debugMode && hasTimeDim !== -1 ? (
    <ShowTimeAwareLayerInfo
      layer={layer!}
      stepsToCheck={prefetchList}
      visible={visible}
      wmLayer={wmLayer!}
    />
  ) : null;
};
